import * as localForage from 'localforage'
import UniversalStorage from '@vue-storefront/core/store/lib/storage'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus/index'
import { Logger } from '@vue-storefront/core/lib/logger'
import { currentStoreView } from '@vue-storefront/core/lib/multistore';
import rootStore from '@vue-storefront/core/store'
import i18n from '@vue-storefront/i18n'
import { serial, onlineHelper, processURLAddress, timezoneTime } from '@vue-storefront/core/helpers'
import moment from 'moment-timezone'
import getApiEndpointUrl from '@vue-storefront/core/helpers/getApiEndpointUrl'
import { customerAuthHeaders } from '@vue-storefront/core/modules/user/helpers/customerSession'

function notifyError (message: string) {
  rootStore.dispatch('notification/spawnNotification', {
    type: 'error',
    message: i18n.t(message),
    action1: { label: i18n.t('OK') }
  })
}

/**
 * K2-3074: ms-api places a customer's cart only with that customer's token, so a
 * queued order goes out only under the token of the customer who placed it
 * (order.user_id, set at checkout; '' for a guest). A guest order is sent without
 * a token; a customer's order while someone else (or nobody) is signed in is
 * dropped - never sent under another customer's token. An order without user_id
 * (not from checkout) keeps the old behaviour: the current token.
 */
export function queuedOrderSender (order): { token?: string, defer?: boolean, drop?: boolean } {
  const user = (rootStore.state.user || {}) as any
  const ownerId = order.user_id
  if (ownerId === undefined || ownerId === null) {
    return { token: user.token || null }
  }
  if (String(ownerId) === '') {
    return { token: null }
  }
  if (!user.local_data_loaded || (user.token && !user.current)) {
    return { defer: true }
  }
  if (user.token && user.current && String(user.current.id) === String(ownerId)) {
    return { token: user.token }
  }
  return { drop: true }
}

function postOrder (endpoint: string, order, token: string | null) {
  return fetch(processURLAddress(endpoint), {
    method: 'POST',
    headers: Object.assign({ 'Content-Type': 'application/json' }, customerAuthHeaders(token)), // K2-3138: not in the URL
    body: JSON.stringify(order)
  }).then(response => {
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return response.json()
    }
    Logger.error('Error with response - bad content-type!')()
  })
}

/**
 * Sends a queued order; on 401 (expired token) refreshes the token once and
 * resends with the new one.
 */
export function sendQueuedOrder (endpoint: string, order, token: string | null) {
  return postOrder(endpoint, order, token).then(jsonResponse => {
    if (!token || !jsonResponse || jsonResponse.code !== 401) {
      return jsonResponse
    }
    return Promise.resolve(rootStore.dispatch('user/refresh', { failedToken: token }))
      .then(resp => resp && resp.code === 200 && resp.result ? postOrder(endpoint, order, resp.result) : jsonResponse)
      .catch(err => {
        Logger.error('Error refreshing user token for a queued order', err)()
        return jsonResponse
      })
  })
}

function dropQueuedOrder (ordersCollection, orderId, order, reason: string, message: string) {
  Logger.error('Dropping queued order ' + orderId + ': ' + reason, 'order-sync')()
  order.transmited = true // never retry it
  order.transmited_at = timezoneTime()
  order.rejected = reason
  ordersCollection.setItem(orderId.toString(), order)
  notifyError(message)
}

export function beforeRegistration ({ Vue, config, store, isServer }) {
  Vue.prototype.$db.ordersCollection = new UniversalStorage(localForage.createInstance({
    name: 'shop',
    storeName: 'orders'
  }))
  if (!isServer) {
    const orderMutex = {}
    // TODO: move to external file
    EventBus.$on('order/PROCESS_QUEUE', async event => {
      if (onlineHelper.isOnline) {
        Logger.log('Sending out orders queue to server ...')()

        const storeView = currentStoreView()
        const dbNamePrefix = storeView.storeCode ? storeView.storeCode + '-' : ''

        const ordersCollection = new UniversalStorage(localForage.createInstance({
          name: dbNamePrefix + 'shop',
          storeName: 'orders',
          driver: localForage[config.localForage.defaultDrivers['orders']]
        }))

        const fetchQueue = []
        ordersCollection.iterate((order, id) => {
          // Resulting key/value pair -- this callback
          // will be executed for every item in the
          // database.

          if (!order.transmited && !orderMutex[id]) { // not sent to the server yet
            orderMutex[id] = true
            const config = event.config
            const orderData = order
            const orderId = id
            Logger.log('Pushing out order ' + orderId)()
            const sender = queuedOrderSender(orderData)
            if (sender.defer) {
              // the user session isn't restored yet - try on the next pass
              orderMutex[id] = false
              return
            }
            if (sender.drop) {
              dropQueuedOrder(ordersCollection, orderId, orderData, 'owner_not_signed_in', 'A queued order was not sent because you are not signed in to the account that placed it. Sign in and place the order again.')
              orderMutex[id] = false
              return
            }
            const endpoint = getApiEndpointUrl(config.orders, 'endpoint')
            fetchQueue.push(
              /** @todo refactor order synchronisation to proper handling through vuex actions to avoid code duplication */
              sendQueuedOrder(endpoint, orderData, sender.token)
                .then(jsonResponse => {
                  if (jsonResponse) {
                    Logger.info('Response for: ' + orderId + ' = ' + JSON.stringify(jsonResponse.result))()
                    orderData.transmited = true // by default don't retry to transmit this order
                    orderData.transmited_at = timezoneTime()

                    let contactEmail = 'info@korzyna.com'
                    try {
                      let location = store.getters['locations/getActiveLocation']
                      if (location) {
                        contactEmail = location.office_email
                      }
                    } catch (e) {
                      Logger.error('Error getting location', e)()
                    }

                    if (jsonResponse.code !== 200) {
                      Logger.error(jsonResponse, 'order-sync')()

                      if (jsonResponse.code === 400 || jsonResponse.code === 422) {
                        rootStore.dispatch('notification/spawnNotification', {
                          type: 'error',
                          message: i18n.t('Address provided in checkout contains invalid data. Please check if all required fields are filled in and also contact us on {email} to resolve this issue for future.', { email: contactEmail }),
                          action1: { label: i18n.t('OK') }
                        })
                      } else if (jsonResponse.code === 403) {
                        // K2-3074: the cart is another customer's - retrying can't help
                        orderData.rejected = 'cart_of_another_customer'
                        notifyError('The cart belongs to another customer')
                      } else if (jsonResponse.code === 401) {
                        // K2-3074: still rejected after one token refresh
                        orderData.rejected = 'session_expired'
                        notifyError('Your session has expired. Sign in and place the order again.')
                      } else if (jsonResponse.code === 500 && jsonResponse.result === i18n.t('Error: Error while adding products')) {
                        rootStore.dispatch('notification/spawnNotification', {
                          type: 'error',
                          message: i18n.t('Some products you\'ve ordered are out of stock. Your order has been canceled.'),
                          action1: { label: i18n.t('OK') }
                        })
                      } else {
                        orderData.transmited = false // probably some server related error. Enqueue
                      }
                    } else {
                      // K2-3012: this resync path (order sent here after a transient failure on
                      // the first attempt) never went through order/placeOrder, so it never
                      // committed LAST_ORDER_CONFIRMATION - the mutation the GTM module's
                      // purchase tracking listens for. Without this, an order that failed once
                      // and was retried here was still a real order, just untracked in GA4.
                      rootStore.commit('order/order/LAST_ORDER_CONFIRMATION', { order: orderData, confirmation: jsonResponse.result })
                    }

                    ordersCollection.setItem(orderId.toString(), orderData)
                  } else {
                    Logger.error(jsonResponse)()
                  }
                  orderMutex[id] = false
                }).catch(err => {
                  if (config.orders.offline_orders.notification.enabled) {
                    navigator.serviceWorker.ready.then(registration => {
                      registration.sync.register('orderSync')
                        .then(() => {
                          Logger.log('Order sync registered')()
                        })
                        .catch(error => {
                          Logger.log('Unable to sync', error)()
                        })
                    })
                  }
                  Logger.error('Error sending order: ' + orderId, err)()
                  orderMutex[id] = false
                })
            )
          }
        }, (err) => {
          if (err) Logger.error(err)()
          Logger.log('Iteration has completed')()

          // execute them serially
          serial(fetchQueue)
          Logger.info('Processing orders queue has finished')()
        }).catch(err => {
          // This code runs if there were any errors
          Logger.log(err)()
        })
      }
    })
  }
}
